import React, { useState, useEffect } from "react";
import "./dashboardLayout.css";
import { Link, Navigate, Routes, Route, useLocation, NavLink } from "react-router-dom";
import { toast } from "react-toastify";
import DashboardHome from "../pages/dashboard/dashboardHome";
import Users from "../pages/dashboard/users/users";
import Property from "../pages/dashboard/property/property";
import ViewProperty from "../pages/dashboard/property/viewProperty/viewProperty";
import Beds24Page from "../pages/dashboard/channelManager/Beds24Page";
import PriceParityPage from "../pages/dashboard/channelManager/PriceParityPage";
import CurrencyPage from "../pages/dashboard/channelManager/CurrencyPage";
import CurrencyPricingPage from "../pages/dashboard/channelManager/CurrencyPricingPage";
import AnalyticsPage from "../pages/dashboard/analytics/analytics";
import SettingsPage from "../pages/dashboard/settings/settings";
import ReservationsPage from "../pages/dashboard/reservations/ReservationsPage";
import DeveloperPage from "../pages/dashboard/developer/DeveloperPage";
import CalendarPage from "../pages/dashboard/calendar/CalendarPage";
import PropertyConfigurationImportPage from "../pages/dashboard/property/PropertyConfigurationImportPage";
import { GlobalAdminRouteGuard, PropertyUserDashboardGuard, TenantPropertyRouteGuard } from "../components/TenantRouteGuard";
import {
  MenuIcon,
  HomeIcon,
  CalendarIcon,
  UserGroupIcon,
  LogoutIcon,
  SearchIcon,
  BellIcon,
  ChartBarIcon,
  CogIcon,
  OfficeBuildingIcon,
  LinkIcon,
  AdjustmentsIcon,
  CurrencyDollarIcon,
  CodeIcon,
  UploadIcon,
} from "@heroicons/react/outline";

const DashboardLayout = () => {
  const [sidebarOpen, setSidebarOpen] = useState(window.innerWidth >= 768);
  // Get user info from localStorage (role, full name and initials)
  let userRole = null;
  try {
    const u = localStorage.getItem("user");
    if (u) {
      const userObj = JSON.parse(u);
      userRole = userObj?.role?.role_name || null;
    }
  } catch (e) {
    userRole = null;
  }
  let userFullName = "User";
  let userInitials = "U";
  try {
    const userStr = localStorage.getItem("user");
    if (userStr) {
      const userObj = JSON.parse(userStr);
      userRole = userObj?.role?.role_name || null;
      const first = (userObj?.first_name || "").toString().trim();
      const last = (userObj?.last_name || "").toString().trim();
      if (first || last) {
        userFullName = `${first} ${last}`.trim();
      } else {
        userFullName = userObj?.email || "User";
      }

      const fi = first ? first[0] : "";
      const li = last ? last[0] : "";
      if (fi && li) userInitials = `${fi}${li}`.toUpperCase();
      else if (fi) userInitials = fi.toUpperCase();
      else userInitials = (userFullName[0] || "U").toUpperCase();
    }
  } catch (e) {
    userRole = null;
    userFullName = "User";
    userInitials = "U";
  }

  // Update sidebar state on window resize
  useEffect(() => {
    const handleResize = () => {
      setSidebarOpen(window.innerWidth >= 768);
    };
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  const location = useLocation();
  // Keep Property nav active for both /dashboard/property and /dashboard/view-property/:id
  const isPropertyActive =
    location.pathname.startsWith("/dashboard/property") ||
    location.pathname.startsWith("/dashboard/view-property");

  const handleLogout = async () => {
    try {
      // lazy-import to avoid circular issues
      const { logout } = await import("../services/authUtil");
      const res = await logout();
      if (res && res.message) {
        toast.success(res.message);
      } else {
        toast.success("Logged out");
      }
      // use full reload to clear any in-memory state
      window.location.href = "/";
    } catch (error) {
      console.error("Error during logout:", error);
      // Still redirect to root
      window.location.href = "/";
    }
  };

  // Close sidebar when clicking outside on mobile
  const handleContentClick = () => {
    if (window.innerWidth < 768 && sidebarOpen) {
      setSidebarOpen(false);
    }
  };

  return (
    <div className="flex h-screen bg-gradient-to-br from-slate-50 to-blue-50 overflow-hidden">
      {/* Overlay for mobile only */}
      {sidebarOpen && (
        <div
          className="fixed inset-0 bg-black bg-opacity-50 z-20 md:hidden"
          onClick={handleContentClick}
        ></div>
      )}

      {/* Sidebar */}
      <aside
        className={`${
          sidebarOpen ? "w-64 md:w-80" : "w-0"
        } sidebar-gradient h-screen transition-all duration-300 ease-in-out fixed md:sticky top-0 left-0 z-30 overflow-hidden`}
      >
        <div
          className={`${
            sidebarOpen ? "opacity-100 w-full" : "opacity-0 w-0"
          } transition-all duration-300 relative h-full flex flex-col`}
        >
          {/* Animated background elements */}
          <div className="absolute inset-0 overflow-hidden pointer-events-none">
            <div className="animated-bg-orb-1"></div>
            <div className="animated-bg-orb-2"></div>
          </div>

          {/* Logo Section */}
          <div className="p-6 flex justify-between items-center border-b border-white/20 relative z-10">
            <Link
              to="#"
              className="flex items-center space-x-3 whitespace-nowrap group"
            >
              <div className="logo-icon">
                <OfficeBuildingIcon className="h-7 w-7" />
              </div>
              <div>
                <h1 className="text-xl font-bold logo-text">
                  <span className="text-yellow-400">Channel</span> Manager
                </h1>
                <p className="text-xs text-purple-300">Property Hub</p>
              </div>
            </Link>
            {/* <button
              className="text-white focus:outline-none hover:bg-white/10 p-2 rounded-lg transition-all duration-300 hover:rotate-180"
              onClick={() => setSidebarOpen(false)}
            >
              <XIcon className="h-6 w-6" />
            </button> */}
          </div>

          {/* Property Selector - hide for restricted roles (resort-supervisor / resort-manager) */}
          {!(
            userRole === "resort-supervisor" || userRole === "resort-manager"
          ) && (
            <div className="px-4 my-6 relative z-10">
              <div className="property-selector">
                <div className="flex items-center justify-between">
                  <div className="flex items-center space-x-3 flex-1 min-w-0">
                    <div className="property-icon">
                      <OfficeBuildingIcon className="h-5 w-5 text-white" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-semibold text-white truncate">
                        All Properties
                      </p>
                      <p className="text-xs text-purple-300">
                        Active Dashboard
                      </p>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* Navigation Menu */}
          <div
            className={`flex flex-col h-[calc(100vh-16rem)] justify-between`}
          >
            <nav
              className={`px-4 flex-1 space-y-1 overflow-y-auto relative z-10 custom-scrollbar ${
                userRole === "resort-supervisor" ||
                userRole === "resort-manager"
                  ? "mt-6"
                  : ""
              }`}
            >
              {/* Role-based menu rendering */}
              {userRole === "resort-supervisor" ||
              userRole === "resort-manager" ? (
                <>
                  <NavLink to="/dashboard/property" className={() => `nav-link ${isPropertyActive ? "nav-link-active" : ""}`}><div className="shine-effect"></div><CalendarIcon className="nav-icon" /><span className="nav-text">Property</span></NavLink>
                  <QuickNav to="/dashboard/reservations" label="Bookings" icon={<CalendarIcon className="nav-icon" />} active={location.pathname.startsWith("/dashboard/reservations")} />
                  <QuickNav to="/dashboard/developer" label="Developer" icon={<CodeIcon className="nav-icon" />} active={location.pathname.startsWith("/dashboard/developer")} />
                  <QuickNav to="/dashboard/ota" label="OTA" icon={<LinkIcon className="nav-icon" />} active={location.pathname.startsWith("/dashboard/ota")} />
                  <QuickNav to="/dashboard/price-parity" label="Price parity" icon={<AdjustmentsIcon className="nav-icon" />} active={location.pathname.startsWith("/dashboard/price-parity")} />
                  <QuickNav to="/dashboard/currency" label="Currency" icon={<CurrencyDollarIcon className="nav-icon" />} active={location.pathname.startsWith("/dashboard/currency")} />
                  <QuickNav to="/dashboard/analytics" label="Analytics" icon={<ChartBarIcon className="nav-icon" />} active={location.pathname.startsWith("/dashboard/analytics")} />
                  <QuickNav to="/dashboard/settings" label="Settings" icon={<CogIcon className="nav-icon" />} active={location.pathname.startsWith("/dashboard/settings")} />
                </>
              ) : (
                <>
                  <NavLink
                    to="/dashboard"
                    end
                    className={({ isActive }) =>
                      `nav-link ${isActive ? "nav-link-active" : ""}`
                    }
                  >
                    <div className="shine-effect"></div>
                    <HomeIcon className="nav-icon" />
                    <span className="nav-text">Dashboard</span>
                  </NavLink>

                  <QuickNav to="/dashboard/ota" label="OTA" icon={<LinkIcon className="nav-icon" />} active={location.pathname.startsWith("/dashboard/ota")} />
                  <QuickNav to="/dashboard/price-parity" label="Price parity" icon={<AdjustmentsIcon className="nav-icon" />} active={location.pathname.startsWith("/dashboard/price-parity")} />
                  <QuickNav to="/dashboard/currency" label="Currency" icon={<CurrencyDollarIcon className="nav-icon" />} active={location.pathname.startsWith("/dashboard/currency")} />
                  <QuickNav to="/dashboard/reservations" label="Bookings" icon={<CalendarIcon className="nav-icon" />} active={location.pathname.startsWith("/dashboard/reservations")} />
                  <QuickNav to="/dashboard/developer" label="Developer" icon={<CodeIcon className="nav-icon" />} active={location.pathname.startsWith("/dashboard/developer")} />

                  <NavLink
                    to="/dashboard/property"
                    className={() =>
                      `nav-link ${isPropertyActive ? "nav-link-active" : ""}`
                    }
                  >
                    <div className="shine-effect"></div>
                    <CalendarIcon className="nav-icon" />
                    <span className="nav-text">Property</span>
                  </NavLink>

                  <NavLink
                    to="/dashboard/users"
                    className={({ isActive }) =>
                      `nav-link ${isActive ? "nav-link-active" : ""}`
                    }
                  >
                    <div className="shine-effect"></div>
                    <UserGroupIcon className="nav-icon" />
                    <span className="nav-text">Users</span>
                  </NavLink>

                  <QuickNav to="/dashboard/analytics" label="Analytics" icon={<ChartBarIcon className="nav-icon" />} active={location.pathname.startsWith("/dashboard/analytics")} />

                  <QuickNav to="/dashboard/settings" label="Settings" icon={<CogIcon className="nav-icon" />} active={location.pathname.startsWith("/dashboard/settings")} />
                  {userRole === "super-admin" && <QuickNav to="/dashboard/property/import" label="Import setup" icon={<UploadIcon className="nav-icon" />} active={location.pathname.startsWith("/dashboard/property/import")} />}
                  {userRole === "super-admin" && <QuickNav to="/dashboard/calendar" label="Calendar" icon={<CalendarIcon className="nav-icon" />} active={location.pathname.startsWith("/dashboard/calendar")} />}
                </>
              )}
            </nav>

            {/* Logout link at the bottom */}
            <div className="px-4 pb-6 border-t border-white/20 pt-4 relative z-10">
              <button
                onClick={handleLogout}
                className="logout-link w-full text-left flex items-center gap-3"
                type="button"
                title="Logout"
              >
                <LogoutIcon className="h-5 w-5" />
                <span className="font-medium">Logout</span>
              </button>
            </div>
          </div>
        </div>
      </aside>

      {/* Main content */}
      <main className="flex-1 flex flex-col min-w-0 transition-all duration-300 ease-in-out">
        {/* Topbar */}
        <div className="topbar">
          {/* Animated gradient line */}
          <div className="topbar-gradient-line"></div>

          <div className="px-6 py-4 flex items-center justify-between">
            {/* Left Section */}
            <div className="flex items-center space-x-4">
              <button
                className="topbar-menu-btn"
                onClick={() => setSidebarOpen(!sidebarOpen)}
              >
                <MenuIcon className="h-6 w-6" />
              </button>

              {/* Search Bar */}
              {userRole !== "resort-supervisor" && (
                <div className="relative group hidden md:block">
                  <SearchIcon className="search-icon" />
                  <input
                    type="text"
                    placeholder="Search properties, users..."
                    className="search-input"
                  />
                </div>
              )}
            </div>

            {/* Right Section */}
            <div className="flex items-center space-x-4">
              {/* Stats Pills - Hidden on small screens */}
              <div className="hidden lg:flex items-center space-x-3">
                <div className="stat-pill stat-pill-green">
                  <div className="flex items-center space-x-2">
                    <div className="stat-pulse"></div>
                    <span className="text-sm font-semibold text-green-700">
                      Properties Active
                    </span>
                  </div>
                </div>

                <div className="stat-pill stat-pill-blue">
                  <div className="flex items-center space-x-2">
                    <CalendarIcon className="h-4 w-4 text-blue-600" />
                    <span className="text-sm font-semibold text-blue-700">
                      Dashboard
                    </span>
                  </div>
                </div>
              </div>

              {/* Notifications */}
              <button className="notification-btn">
                <BellIcon className="h-6 w-6" />
                <span className="notification-badge"></span>
              </button>

              {/* Messages */}
              {/* <button className="notification-btn">
                <InboxIcon className="h-6 w-6" />
                <span className="message-count-badge">3</span>
              </button> */}

              {/* Profile */}
              <div className="flex items-center space-x-3 pl-4 border-l border-gray-200">
                <div className="profile-avatar">{userInitials}</div>
                <div className="hidden md:block">
                  <div className="text-sm font-medium text-gray-700">
                    {userFullName}
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Main content area */}
        <div className="flex-1 overflow-y-auto bg-gray-50 p-6">
          <div className="container mx-auto">
            <Routes>
              <Route path="/" element={<PropertyUserDashboardGuard><DashboardHome /></PropertyUserDashboardGuard>} />
              <Route path="/property" element={<Property />} />
              <Route path="/property/import" element={<GlobalAdminRouteGuard><PropertyConfigurationImportPage /></GlobalAdminRouteGuard>} />
              <Route path="/users" element={<PropertyUserDashboardGuard><Users /></PropertyUserDashboardGuard>} />
              <Route path="/view-property/:id" element={<TenantPropertyRouteGuard><ViewProperty /></TenantPropertyRouteGuard>} />
              <Route path="/ota" element={<Beds24Page />} />
              <Route path="/price-parity" element={<PriceParityPage />} />
              <Route path="/currency" element={<CurrencyPage />} />
              <Route path="/currency/pricing" element={<CurrencyPricingPage />} />
              <Route path="/analytics" element={<AnalyticsPage />} />
              <Route path="/settings" element={<SettingsPage />} />
              <Route path="/calendar" element={<GlobalAdminRouteGuard><CalendarPage /></GlobalAdminRouteGuard>} />
              <Route path="/reservations" element={<ReservationsPage />} />
              <Route path="/booking-widget" element={<Navigate to="/developer" replace />} />
              <Route path="/developer/*" element={<DeveloperPage />} />
            </Routes>
          </div>
        </div>
      </main>
    </div>
  );
};

const QuickNav = ({ to, label, icon, active }) => (
  <NavLink to={to} className={`nav-link ${active ? "nav-link-active" : ""}`}>
    <div className="shine-effect"></div>
    {icon}
    <span className="nav-text">{label}</span>
  </NavLink>
);

export default DashboardLayout;
